import { Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import PageBackdrop from "../components/ui/PageBackdrop";

const HomePage = () => {
  const { user } = useUser();
  const firstName = user?.firstName || null;

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Simple market hours check (ET): Mon–Fri 9:30am–4pm
  const day = now.getDay();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const totalMins = hours * 60 + minutes;
  const marketOpen =
    day >= 1 && day <= 5 && totalMins >= 570 && totalMins < 960;

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .hp-root {
          background: var(--bg);
          color: var(--text-primary);
          font-family: var(--font-sans);
          min-height: 100vh;
          position: relative;
          overflow: hidden;
        }

        .hp-inner {
          position: relative;
          z-index: 1;
          max-width: 860px;
          margin: 0 auto;
          padding: 60px 24px 80px;
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        /* ── Greeting ── */
        .hp-greeting {
          opacity: 0;
          animation: fadeUp 0.45s 0.05s ease forwards;
        }

        .hp-greeting-meta {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .hp-market-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: var(--font-mono);
          font-size: 12px;
          padding: 4px 10px;
          border-radius: var(--radius-pill);
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border: 1px solid;
        }

        .hp-market-badge.open {
          color: var(--accent);
          background: var(--accent-soft);
          border-color: var(--accent-border);
        }

        .hp-market-badge.closed {
          color: var(--text-tertiary);
          background: rgba(148,163,184,0.08);
          border-color: rgba(148,163,184,0.18);
        }

        .hp-market-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .hp-market-badge.open .hp-market-dot {
          background: var(--accent);
          animation: pulse 2s infinite;
        }

        .hp-market-badge.closed .hp-market-dot { background: var(--text-tertiary); }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }

        .hp-date {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.06em;
        }

        .hp-title {
          font-family: var(--font-display);
          font-size: clamp(32px, 5vw, 54px);
          font-weight: 400;
          line-height: 1.08;
          letter-spacing: -0.02em;
          color: var(--text-primary);
          margin-bottom: 10px;
        }

        .hp-title em { font-style: italic; color: var(--accent); }

        .hp-sub {
          font-size: 16px;
          color: var(--text-tertiary);
          font-weight: 400;
          line-height: 1.65;
          max-width: 480px;
        }

        /* ── Primary nav cards ── */
        .hp-nav-cards {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          opacity: 0;
          animation: fadeUp 0.45s 0.14s ease forwards;
        }

        .hp-nav-card {
          display: flex;
          flex-direction: column;
          gap: 0;
          background: var(--surface-1);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-lg);
          overflow: hidden;
          text-decoration: none;
          transition: border-color var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out);
          cursor: pointer;
        }

        .hp-nav-card:hover {
          border-color: var(--border-strong);
          transform: translateY(-2px);
        }

        .hp-nav-card.primary:hover {
          border-color: var(--accent-border);
        }

        .hp-nav-card-top {
          padding: 24px 24px 20px;
          flex: 1;
        }

        .hp-nav-card-icon {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          margin-bottom: 16px;
        }

        .hp-nav-card-label {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 6px;
        }

        .hp-nav-card-title {
          font-family: var(--font-display);
          font-size: 20px;
          font-weight: 400;
          color: var(--text-primary);
          letter-spacing: -0.01em;
          margin-bottom: 8px;
          line-height: 1.15;
        }

        .hp-nav-card-desc {
          font-size: 14px;
          color: var(--text-tertiary);
          font-weight: 400;
          line-height: 1.6;
        }

        .hp-nav-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 24px;
          border-top: 1px solid var(--border-subtle);
          background: var(--surface-2);
        }

        .hp-nav-card-action {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--accent);
          letter-spacing: 0.04em;
        }

        .hp-nav-card-arrow {
          color: var(--accent);
          opacity: 0.6;
          transition: transform var(--dur-fast) var(--ease-out), opacity var(--dur-fast) var(--ease-out);
        }

        .hp-nav-card:hover .hp-nav-card-arrow {
          transform: translateX(3px);
          opacity: 1;
        }

        /* ── Stats strip ── */
        .hp-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: var(--border-subtle);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-md);
          overflow: hidden;
          opacity: 0;
          animation: fadeUp 0.45s 0.22s ease forwards;
        }

        .hp-stat {
          background: var(--surface-1);
          padding: 18px 20px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .hp-stat-num {
          font-family: var(--font-display);
          font-size: 22px;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1;
        }

        .hp-stat-label {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        /* ── How it works teaser ── */
        .hp-pipeline {
          display: flex;
          align-items: center;
          gap: 0;
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-md);
          overflow: hidden;
          background: var(--surface-1);
          opacity: 0;
          animation: fadeUp 0.45s 0.3s ease forwards;
        }

        .hp-pipeline-step {
          flex: 1;
          padding: 16px 18px;
          border-right: 1px solid var(--border-subtle);
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .hp-pipeline-step:last-child { border-right: none; }

        .hp-pipeline-num {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .hp-pipeline-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .hp-pipeline-note {
          font-size: 12px;
          color: var(--text-tertiary);
          font-weight: 400;
        }

        .hp-pipeline-sep {
          font-size: 16px;
          color: var(--accent);
          opacity: 0.3;
          padding: 0 2px;
          flex-shrink: 0;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 580px) {
          .hp-inner { padding: 40px 16px 60px; gap: 28px; }
          .hp-nav-cards { grid-template-columns: 1fr; }
          .hp-stats { grid-template-columns: 1fr 1fr; }
          .hp-stats > :last-child { grid-column: span 2; }
          .hp-pipeline { flex-direction: column; }
          .hp-pipeline-step { border-right: none; border-bottom: 1px solid var(--border-subtle); }
          .hp-pipeline-step:last-child { border-bottom: none; }
          .hp-pipeline-sep { display: none; }
        }
      `}</style>

      <div className="hp-root">
        <PageBackdrop variant="compact" />

        <div className="hp-inner">
          {/* ── Greeting ── */}
          <div className="hp-greeting">
            <div className="hp-greeting-meta">
              <span
                className={`hp-market-badge ${marketOpen ? "open" : "closed"}`}
              >
                <span className="hp-market-dot" />
                Market {marketOpen ? "Open" : "Closed"}
              </span>
              <span className="hp-date">{dateStr}</span>
            </div>
            <h1 className="hp-title">
              {firstName ? (
                <>
                  Good to see you, <em>{firstName}.</em>
                </>
              ) : (
                <>Good to see you.</>
              )}
            </h1>
            <p className="hp-sub">
              Today's screener has already run. Every stock that made the cut is
              waiting.
            </p>
          </div>

          {/* ── Primary nav cards ── */}
          <div className="hp-nav-cards">
            <Link to="/stock-list" className="hp-nav-card primary">
              <div className="hp-nav-card-top">
                <div className="hp-nav-card-icon">
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path
                      d="M2 13l4-4.5 3.5 3 4.5-7"
                      stroke="#34d399"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="hp-nav-card-label">Daily list</div>
                <div className="hp-nav-card-title">Today's Stock List</div>
                <div className="hp-nav-card-desc">
                  Every stock that passed today's screen, ranked by quality and
                  valuation.
                </div>
              </div>
              <div className="hp-nav-card-footer">
                <span className="hp-nav-card-action">View the list</span>
                <svg
                  className="hp-nav-card-arrow"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M2.5 7h9M7.5 2.5l4.5 4.5-4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Link>

            <Link to="/chat" className="hp-nav-card">
              <div className="hp-nav-card-top">
                <div className="hp-nav-card-icon">
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <circle
                      cx="7.5"
                      cy="7.5"
                      r="5"
                      stroke="#34d399"
                      strokeWidth="1.4"
                    />
                    <path
                      d="M11.5 11.5l3.5 3.5"
                      stroke="#34d399"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div className="hp-nav-card-label">Chat</div>
                <div className="hp-nav-card-title">Ask Max Stock Machine</div>
                <div className="hp-nav-card-desc">
                  Full valuation, quality scores, and analyst notes for any
                  U.S.-listed company - just ask.
                </div>
              </div>
              <div className="hp-nav-card-footer">
                <span className="hp-nav-card-action">Start chatting</span>
                <svg
                  className="hp-nav-card-arrow"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M2.5 7h9M7.5 2.5l4.5 4.5-4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Link>
          </div>

          {/* ── Stats ── */}
          <div className="hp-stats">
            <div className="hp-stat">
              <div className="hp-stat-num">3,000+</div>
              <div className="hp-stat-label">Stocks scanned daily</div>
            </div>
            <div className="hp-stat">
              <div className="hp-stat-num">~200</div>
              <div className="hp-stat-label">Pass the screener</div>
            </div>
            <div className="hp-stat">
              <div className="hp-stat-num">Best Stocks in the US</div>
              <div className="hp-stat-label">AI-ranked picks</div>
            </div>
          </div>

          {/* ── Pipeline teaser ── */}
          <div className="hp-pipeline">
            <div className="hp-pipeline-step">
              <span className="hp-pipeline-num">01</span>
              <span className="hp-pipeline-label">Universe Scan</span>
              <span className="hp-pipeline-note">Every U.S. stock</span>
            </div>
            <div className="hp-pipeline-step">
              <span className="hp-pipeline-num">02</span>
              <span className="hp-pipeline-label">DCF + EPV Screen</span>
              <span className="hp-pipeline-note">
                Valuation + red-flag filter
              </span>
            </div>
            <div className="hp-pipeline-step">
              <span className="hp-pipeline-num">03</span>
              <span className="hp-pipeline-label">Business Rater</span>
              <span className="hp-pipeline-note">LLM quality scoring</span>
            </div>
            <div className="hp-pipeline-step">
              <span className="hp-pipeline-num">04</span>
              <span className="hp-pipeline-label">Daily List</span>
              <span className="hp-pipeline-note">Top stocks saved by open</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
